import React from 'react';
import { motion } from 'motion/react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SocialShare } from '@/components/ui/SocialShare';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useRegion } from '@/contexts/RegionContext';
import { LazyImage } from '@/components/ui/LazyImage';

export function LensPersonalMasteryPage() {
  const { t } = useTranslation();
  const { regionCode } = useRegion();

  const title = 'Personal Mastery & Maturity Lens | Vertical Development | FIVE LENS';
  const description = 'Measure resilience, self-efficacy, and leadership maturity. The Personal Mastery lens assesses vertical development and capacity for organisational complexity.';

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: t('nav.framework', 'The Framework'), href: '/#framework' },
          { label: t('framework.lens2.title', 'Personal Mastery & Maturity') }
        ]} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-6 flex items-center gap-3">
            <span className="h-[1px] w-8 bg-rule-3"></span>
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">Lens 02</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-serif leading-tight mb-8 text-ink font-normal">
            Personal Mastery & Maturity
          </h1>
          
          <div className="prose prose-lg prose-brand max-w-none text-ink/70/80 leading-relaxed font-light">
            <p className="text-xl mb-4 font-normal text-ink">
              The Personal Mastery lens focuses on the internal drivers that influence how individuals think, relate, and act. Inspired by the work of Peter Senge, it assesses the mental, emotional, and motivational factors that contribute to personal maturity and the ability to navigate life with self-awareness and stability.
            </p>
            <p className="mb-8">
              Rather than positioning mastery as a trait one “has,” this lens frames it as a lifelong discipline — the ongoing ability to stay grounded, learn continuously, and respond rather than react. Individuals with high personal mastery are not defined by perfection, but by their capacity to reflect, adapt, and grow.
            </p>
            
            <LazyImage 
              src="/personal-mastery-DETAILED.webp" 
              alt="Personal Mastery and Maturity lens diagram showing self-efficacy, resilience, and vertical leadership development dimensions" 
              className="w-full max-w-md mx-auto my-12 object-contain mix-blend-multiply"
            />
            
            <h3 className="text-2xl font-serif text-ink mb-4">How This Lens Fits Into the FIVE LENS Framework</h3>
            <p className="mb-4">
              While the Enneagram provides insight into observable behaviour, it doesn’t always reveal the deeper emotional beliefs and patterns that drive those behaviours. That’s where the Personal Mastery lens comes in.
            </p>
            <p className="mb-8">
              By surfacing beliefs related to purpose, resilience, and connection, this lens adds a critical internal layer to the FIVE LENS model — allowing coaches and leaders to move beyond behavioural observation into true transformation. It gives language to a person’s readiness for growth and highlights the internal obstacles that may limit their development.
            </p>

            <h3 className="text-2xl font-serif text-ink mb-4">How It Supports Coaching & Organisational Development</h3>
            <p className="mb-4">
              The Personal Mastery lens helps uncover the thought patterns, emotional habits, and internal narratives that shape how individuals engage with the world. In both coaching and organisational contexts, it offers a structured way to work with limiting beliefs, support mindset shifts, and develop greater emotional maturity.
            </p>
            <p className="mb-8">
              Whether applied in one-on-one coaching or as part of a leadership or wellbeing initiative, this lens enhances self-leadership, psychological resilience, and the capacity to grow in complexity.
            </p>

            <div className="bg-white p-8 md:p-12 border border-ground-deep my-12">
              <h3 className="text-2xl font-serif text-ink mb-6">Core Dimensions of Mastery</h3>
              <p className="mb-6">Personal Mastery is not a destination, but a continuous practice of refining how we engage with our internal world and the external environment. FIVE LENS measures across several vital dimensions:</p>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Conscious Intent</h4>
                  <p className="text-sm">The ability to align daily actions with a deeper sense of purpose and long-term vision, rather than being swept away by immediate pressures or organizational noise.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Emotional Agility</h4>
                  <p className="text-sm">The capacity to experience complex, difficult emotions without being controlled by them. It involves moving fluidly through challenges rather than getting stuck in rigid defensive patterns.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Systemic Awareness</h4>
                  <p className="text-sm">Recognizing that we do not operate in a vacuum. It is the ability to see the interconnectedness of our actions, our team's dynamics, and the broader organizational ecosystem.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Authentic Alignment</h4>
                  <p className="text-sm">Closing the gap between our espoused values (what we say we believe) and our values-in-action (how we actually behave under stress).</p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-serif text-ink mb-4">The Shift: From Reactive to Proactive</h3>
            <p className="mb-8">A key indicator of high Personal Mastery is the widening of the space between <em>stimulus</em> and <em>response</em>. In lower states of maturity, leaders often operate on autopilot, reacting impulsively to triggers based on their Enneagram style's defense mechanisms. As Personal Mastery increases, individuals develop the capacity to pause, reflect, and choose a conscious, constructive response, even in high-stakes environments.</p>

            <div className="space-y-6 mb-12">
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">Reactive State (Low Mastery)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Defensive, driven by unconscious fears, blaming external circumstances, seeking immediate gratification or relief from anxiety.</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">Transitional State (Growing Mastery)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Aware of triggers but still occasionally caught by them. Capable of course-correcting after a reaction and taking accountability for the impact.</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="md:w-1/3 font-bold text-rule-3">Proactive State (High Mastery)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Grounded, objective, and resilient. Able to hold multiple perspectives, regulate the nervous system, and act in alignment with core values.</p></div>
              </div>
            </div>

            <h3 className="text-2xl font-serif text-ink mb-4">Driving Outcomes</h3>
            <p className="mb-8">
              By revealing thought patterns and emotional habits, Personal Mastery helps leaders address limiting beliefs and foster significant mindset shifts. It is incredibly valuable for enhancing self-leadership, psychological resilience, and the capacity for continuous growth, in both executive coaching and enterprise-wide development contexts.
            </p>
            <SocialShare title={`FIVE LENS Framework: ${t('framework.lens2.title', 'Personal Mastery & Maturity')}`} />
          </div>
        </motion.div>
      </div>
    </main>
  );
}
